import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle, XCircle, Upload, ArrowRight, Pencil, Save, X, Database, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CourthouseSheetScannerProps {
  county: string;
  countyLabel: string;
  onRecordsUpdated?: () => void;
  onViewUpdatedRecords?: () => void;
}

interface ExtractedRow {
  row_number: number;
  filing_date: string;
  doc_number?: string;
  seller_name: string;
  address: string | null;
  status: 'ready' | 'saved' | 'removed' | 'error';
  errorMessage?: string;
}

export function CourthouseSheetScanner({ county, countyLabel, onRecordsUpdated, onViewUpdatedRecords }: CourthouseSheetScannerProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState<ExtractedRow[]>([]);
  const [totalSaved, setTotalSaved] = useState(0);
  const [sheetsScanned, setSheetsScanned] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editField, setEditField] = useState<'seller_name' | 'address' | 'filing_date'>('address');
  const [editValue, setEditValue] = useState("");
  const [integrated, setIntegrated] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setImageData(event.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => setImageData(event.target?.result as string);
          reader.readAsDataURL(blob);
        }
      }
    }
  };

  const handleScan = async () => {
    if (!imageData) return;
    setIsProcessing(true);
    setRows([]);
    setIntegrated(false);

    try {
      const { data, error } = await supabase.functions.invoke("scan-courthouse-sheet", {
        body: { imageData, county },
      });

      if (error) throw error;

      if (data.success && data.rows) {
        const extracted: ExtractedRow[] = data.rows.map((r: any) => ({
          row_number: r.row_number,
          filing_date: r.filing_date || '',
          doc_number: r.doc_number || undefined,
          seller_name: r.seller_name || '',
          address: r.address || null,
          status: 'ready' as const,
        }));
        setRows(extracted);
        setSheetsScanned(prev => prev + 1);
        toast.success(`Sheet ${sheetsScanned + 1}: Extracted ${extracted.length} rows — review and save`);
      } else {
        throw new Error(data.error || "Scan failed");
      }
    } catch (error: any) {
      console.error("Scan error:", error);
      toast.error(error.message || "Failed to scan sheet");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAll = async () => {
    const readyRows = rows.filter(r => r.status === 'ready' && r.seller_name);
    if (readyRows.length === 0) {
      toast.error("No rows to save");
      return;
    }

    setIsSaving(true);
    try {
      const records = readyRows.map(r => ({
        county: county.toLowerCase(),
        document_number: r.doc_number || `${county.toUpperCase()}-${r.filing_date}-${r.seller_name.replace(/[^a-zA-Z]/g, '').slice(0, 10)}`,
        filing_date: normalizeDate(r.filing_date),
        document_type: 'Land Contract',
        grantor_name: r.seller_name.toUpperCase(),
        grantor_address: r.address || null,
        is_potential_owner_finance: true,
        status: r.address ? 'reviewed' : 'new',
        address_extraction_method: r.address ? 'courthouse_handwritten' : null,
        search_date: new Date().toISOString(),
      }));

      // Check for duplicates before inserting
      let saved = 0;
      let skipped = 0;
      const updatedRows = [...rows];

      for (let i = 0; i < records.length; i++) {
        const rec = records[i];
        const rowIdx = rows.findIndex(r => r.status === 'ready' && r.seller_name && 
          r.seller_name.toUpperCase() === rec.grantor_name && r.filing_date === rows[rows.indexOf(rows.filter(x => x.status === 'ready')[i])]?.filing_date);
        
        // Check if record already exists
        const { data: existing } = await supabase
          .from('county_research')
          .select('id')
          .eq('county', county.toLowerCase())
          .eq('filing_date', rec.filing_date)
          .ilike('grantor_name', rec.grantor_name)
          .is('deleted_at', null)
          .maybeSingle();

        if (existing) {
          // Update address if we have one and existing doesn't
          if (rec.grantor_address) {
            await supabase
              .from('county_research')
              .update({
                grantor_address: rec.grantor_address,
                address_extraction_method: 'courthouse_handwritten',
                address_needs_review: false,
              })
              .eq('id', existing.id);
            saved++;
          } else {
            skipped++;
          }
        } else {
          const { error } = await supabase.from('county_research').insert(rec);
          if (error) {
            console.error(`Insert error:`, error);
          } else {
            saved++;
          }
        }
      }

      // Mark all ready rows as saved
      setRows(prev => prev.map(r => r.status === 'ready' ? { ...r, status: 'saved' as const } : r));
      setTotalSaved(prev => prev + saved);
      setIntegrated(true);
      toast.success(`✅ Saved ${saved} records${skipped > 0 ? `, ${skipped} duplicates skipped` : ''}`);
      onRecordsUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const removeRow = (index: number) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, status: 'removed' as const } : r));
  };

  const startEdit = (index: number, field: 'seller_name' | 'address' | 'filing_date') => {
    setEditingIndex(index);
    setEditField(field);
    setEditValue(field === 'address' ? (rows[index].address || '') : field === 'seller_name' ? rows[index].seller_name : rows[index].filing_date);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    setRows(prev => prev.map((r, i) => {
      if (i !== editingIndex) return r;
      if (editField === 'address') return { ...r, address: editValue.trim() || null };
      if (editField === 'seller_name') return { ...r, seller_name: editValue.trim() };
      if (editField === 'filing_date') return { ...r, filing_date: editValue.trim() };
      return r;
    }));
    setEditingIndex(null);
    setEditValue("");
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') { setEditingIndex(null); setEditValue(""); }
  };

  const handleNextSheet = () => {
    setImageData(null);
    setRows([]);
    setIntegrated(false);
    setEditingIndex(null);
  };

  const handleClear = () => {
    setRows([]);
    setImageData(null);
    setIntegrated(false);
    toast.info("Cleared (database not affected)");
  };

  const readyCount = rows.filter(r => r.status === 'ready').length;
  const savedCount = rows.filter(r => r.status === 'saved').length;

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Scan Courthouse Handwritten Sheet
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload photos of handwritten sheets from {countyLabel}. All data (dates, names, addresses) will be extracted and <strong>created as new records</strong>.
          </p>
        </div>
        {rows.length > 0 && !integrated && (
          <Button variant="outline" size="sm" onClick={handleClear} className="text-muted-foreground shrink-0">
            <Trash2 className="mr-2 h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {sheetsScanned > 0 && (
        <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-4 text-sm">
          <span className="font-medium">Session Progress:</span>
          <Badge variant="secondary">{sheetsScanned} sheet{sheetsScanned !== 1 ? 's' : ''} scanned</Badge>
          <Badge variant="default" className="bg-green-600">{totalSaved} total saved</Badge>
        </div>
      )}

      <div
        className="border-2 border-dashed border-border rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none"
        onPaste={handlePaste}
        tabIndex={0}
      >
        {!imageData ? (
          <div className="space-y-3">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">
              {sheetsScanned > 0
                ? `Ready for sheet ${sheetsScanned + 1} — upload or paste next photo`
                : "Upload a photo of the handwritten courthouse sheet"}
            </p>
            <div className="flex gap-2 justify-center">
              <label>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span><Upload className="mr-2 h-4 w-4" />Upload Photo</span>
                </Button>
              </label>
              <label>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span>📁 Choose File</span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Or click here and press Ctrl+V to paste</p>
          </div>
        ) : (
          <div className="space-y-4">
            <img src={imageData} alt="Courthouse sheet" className="max-h-96 mx-auto rounded border" />
            <div className="flex gap-2 justify-center">
              {rows.length === 0 ? (
                <>
                  <Button onClick={handleScan} disabled={isProcessing} size="lg">
                    {isProcessing ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reading Handwriting...</>
                    ) : (
                      <><CheckCircle className="mr-2 h-4 w-4" />Scan & Preview</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setImageData(null)} disabled={isProcessing}>
                    Clear
                  </Button>
                </>
              ) : (
                <>
                  {!integrated && readyCount > 0 && (
                    <Button onClick={handleSaveAll} disabled={isSaving} size="lg" className="bg-green-600 hover:bg-green-700">
                      {isSaving ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                      ) : (
                        <><Database className="mr-2 h-4 w-4" />Save {readyCount} Records</>
                      )}
                    </Button>
                  )}
                  <Button onClick={handleNextSheet} size="lg" variant={integrated ? "default" : "outline"}>
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Scan Next Sheet
                  </Button>
                  {onViewUpdatedRecords && integrated && (
                    <Button variant="outline" onClick={onViewUpdatedRecords}>
                      View Records
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            {!integrated && readyCount > 0 && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">{readyCount} Ready to Save</Badge>
            )}
            {savedCount > 0 && (
              <Badge variant="default" className="bg-green-600">✅ {savedCount} Saved</Badge>
            )}
            <span className="text-xs text-muted-foreground ml-2">
              {integrated ? "Records saved to database." : "⚠️ Not yet saved — review and click Save"}
            </span>
          </div>

          <div className="max-h-[500px] overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="text-left p-2 w-8">#</th>
                  <th className="text-left p-2 w-24">Filing Date</th>
                  <th className="text-left p-2">Seller Name</th>
                  <th className="text-left p-2">Address</th>
                  <th className="text-left p-2 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  if (r.status === 'removed') return null;
                  return (
                    <tr key={i} className={`border-t ${r.status === 'saved' ? 'bg-green-50' : r.status === 'ready' ? 'bg-amber-50' : ''}`}>
                      <td className="p-2 text-muted-foreground">{r.row_number}</td>
                      <td className="p-2 font-mono text-xs">
                        {editingIndex === i && editField === 'filing_date' ? (
                          <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                        ) : (
                          <span className="cursor-pointer hover:underline" onClick={() => !integrated && startEdit(i, 'filing_date')}>{r.filing_date || '—'}</span>
                        )}
                      </td>
                      <td className="p-2">
                        {editingIndex === i && editField === 'seller_name' ? (
                          <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                        ) : (
                          <span className="cursor-pointer hover:underline" onClick={() => !integrated && startEdit(i, 'seller_name')}>{r.seller_name || '—'}</span>
                        )}
                      </td>
                      <td className="p-2 text-xs">
                        {editingIndex === i && editField === 'address' ? (
                          <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                        ) : (
                          <span className={`cursor-pointer hover:underline ${!r.address ? 'text-muted-foreground italic' : ''}`} onClick={() => !integrated && startEdit(i, 'address')}>
                            {r.address || 'No address'}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        {r.status === 'saved' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : editingIndex === i ? (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={saveEdit}><Save className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingIndex(null); setEditValue(""); }}><X className="h-3 w-3" /></Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEdit(i, 'address')}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeRow(i)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function normalizeDate(dateStr: string): string {
  if (!dateStr) return new Date().toISOString().split('T')[0];
  
  // Try M/D/YY or M/D/YYYY
  const mdyMatch = dateStr.match(/^(\d{1,2})\/?(\d{1,2})\/?(\d{2,4})$/);
  if (mdyMatch) {
    let year = mdyMatch[3];
    if (year.length === 2) {
      const num = parseInt(year);
      year = num > 50 ? `19${year}` : `20${year}`;
    }
    return `${year}-${mdyMatch[1].padStart(2, '0')}-${mdyMatch[2].padStart(2, '0')}`;
  }
  
  // Already YYYY-MM-DD
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  
  return dateStr;
}
